import React, { useEffect } from 'react';
import { App, Modal } from 'antd';
import { colors } from '@/lib/antd-config';
import type { ModalFuncProps } from 'antd';

export interface ConfirmActionOptions {
  title?: string;
  content: React.ReactNode | string;
  okText?: string;
  cancelText?: string;
  okButtonProps?: ModalFuncProps['okButtonProps'];
  width?: number;
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
  isDangerous?: boolean;
}

// Use App's modal when available so confirm consumes theme/context (avoids antd warning)
let appModal: ReturnType<typeof App.useApp>['modal'] | null = null;
export function setConfirmModal(modal: ReturnType<typeof App.useApp>['modal'] | null) {
  appModal = modal;
}

/**
 * Shows a confirmation modal using Ant Design Modal.confirm
 * Returns a promise that resolves when the user confirms or cancels
 * 
 * @param options - Configuration options for the confirmation modal
 * @returns Promise that resolves when the modal is closed
 * 
 * @example
 * Simple confirmation:
 * ```tsx
 * await confirmAction({
 *   content: 'Are you sure you want to delete this item?',
 *   onConfirm: async () => {
 *     await deleteItem();
 *   },
 *   isDangerous: true,
 * });
 * ```
 * 
 * @example
 * With custom title and context:
 * ```tsx
 * await confirmAction({
 *   title: 'Delete User',
 *   content: createConfirmationContent(
 *     'This action cannot be undone.',
 *     'This will delete the user account and all associated data.'
 *   ),
 *   onConfirm: async () => {
 *     await deleteUser(userId);
 *     message.success('User deleted successfully');
 *   },
 *   isDangerous: true,
 * });
 * ```
 * 
 * @example
 * With custom button text:
 * ```tsx
 * await confirmAction({
 *   content: 'Save changes?',
 *   okText: 'Save',
 *   cancelText: 'Discard',
 *   onConfirm: async () => {
 *     await saveChanges();
 *   },
 * });
 * ```
 */
export const confirmAction = (options: ConfirmActionOptions): Promise<void> => {
  const {
    title = 'Confirm Action',
    content,
    okText = 'Confirm',
    cancelText = 'Cancel',
    okButtonProps,
    width = 520,
    onConfirm,
    onCancel,
    isDangerous = false,
  } = options;

  return new Promise<void>((resolve) => {
    const confirmOptions = {
      title,
      content,
      okText,
      cancelText,
      okButtonProps: {
        danger: isDangerous,
        ...okButtonProps,
      },
      width,
      onOk: async () => {
        try {
          await onConfirm();
        } catch (error) {
          console.error('Error in confirmation action:', error);
        }
        resolve();
      },
      onCancel: () => {
        onCancel?.();
        resolve();
      },
    };
    if (appModal?.confirm) {
      appModal.confirm(confirmOptions);
    } else {
      Modal.confirm(confirmOptions);
    }
  });
};

/**
 * Renders inside Ant Design's <App> and wires confirmAction to use App's modal
 * so confirm modals consume theme/context. Must be a child of antd <App>.
 */
export function ConfirmModalBridge() {
  const { modal } = App.useApp();
  useEffect(() => {
    setConfirmModal(modal);
    return () => setConfirmModal(null);
  }, [modal]);
  return null;
};

/**
 * Helper function to create a confirmation message with additional context
 * 
 * @param message - The main confirmation message
 * @param context - Additional context to display (e.g., item count)
 * @returns React node with formatted confirmation content
 */
export const createConfirmationContent = (
  message: string,
  context?: string | React.ReactNode
): React.ReactNode => {
  if (!context) {
    return message;
  }

  return (
    <>
      <div>{message}</div>
      {context && (
        <div style={{ marginTop: 8, color: colors.textMuted }}>
          {context}
        </div>
      )}
    </>
  );
};
